import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { ChevronsUpDown, Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { useGetPropertiesQuery } from "@/store/rest_api";
import Property from "@/model/interface/Property";

interface PropertyComboboxProps {
  value: Property | null;
  onChange: (property: Property | null) => void;
}

function propertyLabel(property: Property): string {
  return [property.address, property.city, property.state].filter(Boolean).join(", ");
}

export default function PropertyCombobox({ value, onChange }: PropertyComboboxProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const { data, isFetching } = useGetPropertiesQuery({ size: 20, q: search || undefined });
  const properties = data?.items ?? [];

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" aria-expanded={open} className="w-full justify-between font-normal">
          <span className="truncate">{value ? propertyLabel(value) : "Select a property..."}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput placeholder="Search properties..." value={search} onValueChange={setSearch} />
          <CommandList>
            <CommandEmpty>{isFetching ? "Searching..." : "No properties found."}</CommandEmpty>
            <CommandGroup>
              {value && (
                <CommandItem
                  value="__clear__"
                  onSelect={() => {
                    onChange(null);
                    setOpen(false);
                  }}
                >
                  <Check className="mr-2 h-4 w-4 opacity-0" />
                  <span className="text-muted-foreground">Clear selection</span>
                </CommandItem>
              )}
              {properties.map((property) => (
                <CommandItem
                  key={property.id}
                  value={property.id}
                  onSelect={() => {
                    onChange(property.id === value?.id ? null : property);
                    setOpen(false);
                  }}
                >
                  <Check className={cn("mr-2 h-4 w-4", value?.id === property.id ? "opacity-100" : "opacity-0")} />
                  <span className="truncate">{propertyLabel(property)}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
